import React, { useEffect, useRef, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function AIAssistant() {
  const { t } = useLang();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    if (!input.trim()) return;
    const userMsg = { role: "user", content: input, at: new Date().toISOString() };
    setMessages((m) => [...m, userMsg]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/ai/chat", { message: userMsg.content, session_id: sessionId });
      if (!sessionId) setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply, at: new Date().toISOString() }]);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "AI error");
    } finally { setLoading(false); }
  };

  return (
    <DashboardLayout title={t("ai.title")} subtitle={t("ai.subtitle")}>
      <div className="max-w-3xl mx-auto rounded-xl border border-zinc-800 bg-zinc-900/40 flex flex-col h-[calc(100vh-220px)]" data-testid="ai-chat">
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-4">
          {messages.length === 0 && (
            <div className="text-center text-zinc-500 mt-8">
              <Sparkles className="w-8 h-8 mx-auto text-blue-400 mb-3" />
              <div className="text-sm">Hola {user?.name?.split(" ")[0] || ""} — {t("ai.placeholder")}</div>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-xl mx-auto">
                {[
                  { key: "answer", label: "Answer client questions", prompt: "Draft a friendly, professional reply to a client asking about our SEO retainer packages and typical delivery timeline." },
                  { key: "quote", label: "Generate a quote", prompt: "Generate a detailed quote for a small business website: 6 pages, CMS, SEO baseline, hosting for 1 year. Include line items, subtotal and total." },
                  { key: "summarize", label: "Summarize a project", prompt: "Summarize the status of the current highest-priority project in a 3-bullet update for the client." },
                  { key: "internal", label: "Internal business assistant", prompt: "Suggest 5 concrete actions I can take this week to improve monthly recurring revenue." },
                ].map((s) => (
                  <button key={s.key} data-testid={`ai-preset-${s.key}`} onClick={() => setInput(s.prompt)} className="text-left px-3 py-2 rounded-md border border-zinc-800 hover:border-blue-500/50 hover:bg-zinc-900 text-zinc-300 text-xs">
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={m.id || `${m.role}-${i}-${(m.content || "").slice(0, 24)}`} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`} data-testid={`ai-msg-${i}`}>
              {m.role !== "user" && <div className="w-7 h-7 shrink-0 rounded-md bg-blue-500/10 text-blue-400 grid place-items-center"><Sparkles className="w-3.5 h-3.5" /></div>}
              <div className={`max-w-[80%] rounded-lg px-4 py-2.5 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-blue-500 text-white" : "bg-zinc-950 border border-zinc-800 text-zinc-200"}`}>
                {m.content}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex gap-3">
              <div className="w-7 h-7 rounded-md bg-blue-500/10 text-blue-400 grid place-items-center"><Sparkles className="w-3.5 h-3.5" /></div>
              <div className="bg-zinc-950 border border-zinc-800 rounded-lg px-4 py-2.5 text-sm text-zinc-500 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Thinking…</div>
            </div>
          )}
        </div>
        <div className="border-t border-zinc-800 p-4">
          <div className="flex gap-2">
            <input
              data-testid="ai-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={t("ai.placeholder")}
              className="flex-1 h-10 bg-zinc-950 border border-zinc-800 rounded-md px-3 text-sm"
            />
            <button data-testid="ai-send" onClick={send} disabled={loading || !input.trim()} className="btn-scale h-10 px-4 rounded-md bg-blue-500 text-white text-sm font-medium hover:bg-blue-400 disabled:opacity-50 flex items-center gap-1">
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
